import { useCallback, useMemo } from 'react';

import debounce from 'lodash-es/debounce';
import { FieldValues, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { updateAction } from '@/redux/nodes/actions';
import { ChannelPassthrough } from '@/shared/types/action';
import { IntegrationType } from '@/shared/types/integration';

interface PassthroughFormType extends FieldValues {
  selectedChannel: string;
  passthroughPayload: string;
}

interface PassthroughProps {
  /**
   * The action ID to update in Redux
   */
  actionId: string;
  /**
   * Current passthrough data - can be array (for text actions) or single object (for carousel actions)
   */
  passthrough?: ChannelPassthrough[];
}

/**
 * Passthrough Component
 *
 * Reusable component for handling passthrough data configuration.
 * Allows users to select a channel and enter custom payload data
 * that gets sent along with actions. Changes are debounced and
 * automatically saved to Redux.
 *
 * @param actionId - The action ID to update in Redux
 * @param passthrough - Current passthrough data (array)
 * @returns JSX.Element - The passthrough form interface
 */
const Passthrough = ({ actionId, passthrough }: PassthroughProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const form = useForm<PassthroughFormType>({
    mode: 'onChange',
    defaultValues: {
      // If there is not passthrough data, the dropdown will show whatsapp by default.
      // Otherwise it will show the first channel in the list by default.
      selectedChannel: passthrough?.[0]?.channel || 'whatsapp',
      passthroughPayload: passthrough?.[0]?.payload || '',
    },
  });

  const { control, setValue } = form;
  const selectedChannel = useWatch({ control, name: 'selectedChannel' }) as IntegrationType;

  const debouncedUpdatePassthrough = useMemo(
    () =>
      debounce((channel: IntegrationType, payload: string) => {
        if (!channel) {
          return;
        }

        const trimmed = payload.trim();

        const index = passthrough.findIndex((p) => p.channel === channel);
        const newEntry = { channel, payload: trimmed };
        const newList = [...passthrough];

        if (trimmed) {
          if (index >= 0) {
            newList[index] = newEntry;
          } else {
            newList.push(newEntry);
          }
        } else {
          // If the payload is empty, remove the entry
          if (index < 0) {
            return;
          }
          newList.splice(index, 1);
        }

        dispatch(updateAction({ actionId, action: { passthrough: newList } }));
      }, 200),
    [actionId, dispatch, passthrough]
  );

  // Handle channel selection changes
  const handleChannelChange = useCallback(
    (value: string) => {
      setValue('selectedChannel', value);

      // Find the payload for the selected channel
      const channelEntry = passthrough.find((item) => item.channel === value);
      const payloadForChannel = channelEntry?.payload || '';

      setValue('passthroughPayload', payloadForChannel);
    },
    [setValue, passthrough]
  );

  return (
    <Form {...form}>
      <form className="space-y-4">
        <FormField
          control={form.control}
          name="selectedChannel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('common.channel')}</FormLabel>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleChannelChange(value);
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="whatsapp">{t('channels.whatsapp')}</SelectItem>
                  <SelectItem value="sunco">{t('channels.sunco')}</SelectItem>
                  <SelectItem value="viber">{t('channels.viber')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="passthroughPayload"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('common.passthrough_payload')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={'{ ... }'}
                  className="min-h-48 resize-none"
                  onChange={(e) => {
                    field.onChange(e);
                    debouncedUpdatePassthrough(selectedChannel, e.target.value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default Passthrough;
